/**
 * PRP-FEEDBACK-2 (B-7516a): attach an import that has no project to one of the user's projects,
 * or to a new one, after the post-parse dialog is gone. The caller links it
 * (`BackendAPI.linkImportToProject`) through the same `associateProject` path the post-parse
 * dialog uses, so the import then shows on the dashboard under that project.
 */
import { useEffect, useMemo, useState } from 'react';

import { BackendAPI } from '../../lib/api';
import { getProjectDisplayName } from '../../lib/utils';
import type { EstimateHeaderMetadata, Project } from '../../types';
import CreateProjectForm, { type CreateProjectInitialValues } from './CreateProjectForm';
import { IconCheck, IconPlus } from './icons';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Modal } from './ui/modal';

export interface AddToProjectDialogProps {
  /** The import's header, to pre-fill a new project. */
  header: EstimateHeaderMetadata | null | undefined;
  /** The user picked an existing project or created one. */
  onPick: (_project: Project) => void;
  onClose: () => void;
}

function initialValues(header: EstimateHeaderMetadata | null | undefined): CreateProjectInitialValues {
  return {
    street: header?.address?.street ?? '',
    city: header?.address?.city ?? '',
    state: header?.address?.state ?? '',
    zip: header?.address?.zip ?? '',
    owner_name: header?.owner_name ?? '',
    claim_number: header?.claim_number ?? '',
    policy_number: header?.policy_number ?? '',
    loss_type: header?.loss_type ?? '',
    claim_type: header?.claim_type ?? '',
  };
}

const MAX_ROWS = 50;

export default function AddToProjectDialog({ header, onPick, onClose }: AddToProjectDialogProps) {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(BackendAPI.getProjects())
      .then((rows) => {
        if (!cancelled) setProjects(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) {
          setProjects([]);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = projects ?? [];
    const hit = q
      ? rows.filter((p) =>
          [p.name, p.owner_name, p.address, p.claim_number]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q)),
        )
      : rows;
    return hit.slice(0, MAX_ROWS);
  }, [projects, query]);

  if (showCreate) {
    return (
      <CreateProjectForm
        initialValues={initialValues(header)}
        onClose={() => setShowCreate(false)}
        onCreated={onPick}
      />
    );
  }

  return (
    <Modal
      label="Add to project"
      title="Add this estimate to a project"
      subtitle="It then shows on your dashboard under that project."
      onClose={onClose}
      testId="add-to-project-dialog"
      footer={
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)} data-testid="add-to-project-create">
          <IconPlus /> Create new project
        </Button>
      }
    >
      <div className="space-y-2">
        <Input
          aria-label="Find a project"
          placeholder="Find a project by name, insured, address or claim"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {projects === null ? (
          <p className="text-[12.5px] text-[var(--tx-lo)]">Loading your projects…</p>
        ) : failed ? (
          <p className="text-[12.5px] text-[var(--bad)]">Your projects could not be loaded. Create a new one, or try again.</p>
        ) : shown.length === 0 ? (
          <p className="text-[12.5px] text-[var(--tx-lo)]">
            {query ? 'No project matches.' : 'You have no projects yet. Create one.'}
          </p>
        ) : (
          <ul className="max-h-[300px] divide-y divide-[var(--bd-subtle)] overflow-y-auto" data-testid="add-to-project-list">
            {shown.map((p) => (
              <li key={p.id} className="flex items-center gap-3 py-2">
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--tx-hi)]">
                  {getProjectDisplayName(p)}
                  {p.name && p.name !== getProjectDisplayName(p) && (
                    <span className="text-[var(--tx-lo)]"> · {p.name}</span>
                  )}
                  {p.claim_number && <span className="text-[var(--tx-faint)]"> · Claim {p.claim_number}</span>}
                </span>
                <Button variant="secondary" size="sm" onClick={() => onPick(p)} aria-label={`Use ${getProjectDisplayName(p)}`}>
                  <IconCheck /> Use
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
